/* eslint-disable max-lines-per-function */
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'waterfall',
  name_en: 'Waterfall',
  name_zh: '瀑布图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {},
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y2: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    y2: string;
  };
}

export const waterfall = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  withTimeline: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook, layout } = api;
    const chartViewRect = $dom.getBoundingClientRect();
    const presetOption = hook.buildStandardChartPresetOption(api);
    const {
      dataIndex = 0,
      encoding: { x, y, y2 }
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);
    const dataMapKeyByDate = {} as { [k: string]: typeof data };

    // * ------------------------- data
    data.values.forEach(item => {
      const store = dataMapKeyByDate[item[y2]] || [];

      store.push(item);

      dataMapKeyByDate[item[y2]] = store;
    });
    const dates = util.dataTransform.transform(Object.keys(dataMapKeyByDate), [
      { type: 'sort', order: 'ascending' }
    ]);

    // * ------------------------- chart
    const fontSize = presetOption.xAxis.axisLabel?.fontSize || presetOption.textStyle.fontSize;
    const isAxisLabelTilt = [util.const.LAYOUT.SMALL, util.const.LAYOUT.SMALL_1_2].includes(layout);
    const showTimeWatermark = layout !== util.const.LAYOUT.SMALL;

    const legend = [];
    const buildOption = ({
      year,
      dataset
    }: {
      year: string;
      dataset: { [k: string]: number }[];
    }) => {
      const xAxisData = dataset.map(item => item[x]);
      const maxTextWidth = Math.max(
        ...xAxisData.map(item =>
          util.canvas.measureTextWidth(item, fontSize, presetOption.textStyle.fontFamily)
        )
      );
      const fixedTextWidth = Math.min(fontSize * 8, maxTextWidth);
      const grid = {
        top: showTimeWatermark ? 32 : 16,
        left: 0,
        right: 0,
        bottom: 0
      };
      const axisLabelContainerLength =
        (chartViewRect.width - grid.left - grid.right) / xAxisData.length;
      const barWidth = ((chartViewRect.width - grid.left - grid.right) / xAxisData.length) * 0.8;
      const barMaxWidth = util.const.BAR_MAX_WIDTH;

      // 转换数据集
      dataset = ThsDataVStandardChart.helper.WaterfallHelper.transformDataset([
        dataset.map(item => +item[y])
      ]);

      return {
        grid,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              dvMaxWidth: barWidth > barMaxWidth ? barMaxWidth * 1.5 : undefined
            }
          },
          formatter: params_ => {
            const series = Array.isArray(params_) ? params_ : [params_];
            const items = [];

            series.slice(1).forEach(item => {
              const result = hook.formatDataNumberValue(item?.data?.__sign + item.value, {
                type: 'table',
                unit: dataMetaInfo[item.seriesName]?.unit
              });

              items.push({
                type: util.const.SERIES_2_LEGEND_TYPE_MAP[item.seriesType],
                color: item.color,
                // name: item.seriesName,
                name: y,
                value: result.value + result.unit
              });
            });

            return hook.buildTooltip({
              config: hook.buildStandardChartPresetOption(api).tooltip,
              title: series[0]?.axisValueLabel,
              items
            });
          }
        },
        xAxis: [
          {
            show: true,
            type: 'category',
            splitLine: {
              show: false
            },
            axisLine: {
              show: true
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              dvAlignEdge: false,
              interval: 0,
              hideOverlap: false,
              width: Math.max(fixedTextWidth, axisLabelContainerLength),
              overflow: 'truncate',
              rotate: isAxisLabelTilt ? 30 : 0,
              rich: {
                sign: {
                  align: 'left',
                  fontWeight: 400,
                  padding: [0, 0, 0, -4]
                },
                a: {
                  fontWeight: 400
                }
              }
            },
            data: xAxisData
          }
        ],
        yAxis: [
          {
            type: 'value',
            show: false,
            splitLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              show: false
            }
          }
        ],
        series: [
          // 第一个堆叠柱图是为了撑高瀑布图（设置为透明）
          {
            type: 'bar',
            dvType: 'waterfall',
            stack: 'Total',
            barMaxWidth: barWidth > barMaxWidth ? barMaxWidth : undefined,
            itemStyle: {
              borderColor: 'transparent',
              color: 'transparent'
            },
            emphasis: {
              itemStyle: {
                borderColor: 'transparent',
                color: 'transparent'
              }
            },
            data: dataset.dataset[0]
          },
          {
            type: 'bar',
            dvType: 'waterfall',
            stack: 'Total',
            barWidth: '90%',
            barMaxWidth: barWidth > barMaxWidth ? barMaxWidth : undefined,
            label: {
              show: true,
              position: 'top',
              formatter: params_ => {
                const result = hook.formatDataNumberValue(params_?.data?.value, {
                  type: 'table',
                  unit: dataMetaInfo[y]?.unit
                });
                return `${params_?.data?.__sign + result.value + result.unit}`;
              }
            },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return
            data: dataset.dataset[1].map(item => ({
              ...item
            }))
          }
        ],
        graphic: {
          elements: [
            {
              type: 'text',
              right: 20,
              top: 0,
              style: {
                text: `${showTimeWatermark ? year : ''}`,
                fontSize: 18,
                fontWeight: 700,
                fill: '#A8A8A8'
              },
              z: 0
            }
          ]
        }
      };
    };
    let option = buildOption({
      year: dates[dates.length - 1],
      dataset: dataMapKeyByDate[dates[dates.length - 1]]
    });

    return renderApi => {
      option = renderApi.mergeChartOptionProxy(option);

      const timelineIns = renderApi.renderTimelineProxy(dates as string[]);
      renderApi.renderLegendProxy(legend);
      const chartIns = renderApi.renderChartProxy({ option });

      if (timelineIns instanceof api.ui.Dropdown) {
        timelineIns?.on?.('change', eventData => {
          const year = dates[eventData.index];
          option = buildOption({
            year,
            dataset: dataMapKeyByDate[year]
          });
          option = renderApi.mergeChartOptionProxy(option);
          api.hook.beforeRenderStandardChart(api, { option });

          chartIns.play({
            option
          });

          renderApi.cacheRenderOptionProxy(option);
        });
      } else {
        timelineIns?.on?.('change', ({ type, index, timeData }) => {
          const year = timeData;
          option = buildOption({
            year,
            dataset: dataMapKeyByDate[year]
          });
          option = renderApi.mergeChartOptionProxy(option);
          api.hook.beforeRenderStandardChart(api, { option });

          chartIns.play({
            option
          });

          renderApi.cacheRenderOptionProxy(option);
        });
      }
    };
  }
});
